import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { Assert } from "abweb/utils/Assert";
// import ExpOperatorGroup = Expression.Domain.ExpOperatorGroup;
// import ValidatedExpOperator = Expression.Domain.ValidatedExpOperator;

export class OperatorTree {
  constructor(selectedExpOperator, validExpOperators, isSelectedExpOperatorValid, expressionDomainModel) {
    _defineProperty(this, "_selectedExpOperator", void 0);
    _defineProperty(this, "_validExpOperators", void 0);
    _defineProperty(this, "_isSelectedExpOperatorValid", void 0);
    _defineProperty(this, "_expressionDomainModel", void 0);
    _defineProperty(this, "_rootGroupNode", void 0);
    _defineProperty(this, "_groupNodesByOperatorGroupID", void 0);
    this._selectedExpOperator = selectedExpOperator;
    this._validExpOperators = validExpOperators;
    this._isSelectedExpOperatorValid = isSelectedExpOperatorValid;
    this._expressionDomainModel = expressionDomainModel;
    this._rootGroupNode = new OperatorTree.GroupNode();
    this._groupNodesByOperatorGroupID = new Map();
    this.initialize();
  }
  initialize() {
    for (const expOperator of this.expOperators) {
      let groupNode = this.findOrCreateGroupNode(expOperator.groupID);
      groupNode.addChildExpOperator(expOperator);
      while (groupNode !== this._rootGroupNode && groupNode.parentGroupNode == null) {
        const parentGroupNode = this.findOrCreateGroupNode(groupNode.expOperatorGroup.groupID);
        parentGroupNode.addChildGroupNode(groupNode);
        groupNode = parentGroupNode;
      }
    }
  }
  get expOperators() {
    const expOperatorsById = new Map();
    for (const expOperator of [this._selectedExpOperator, ...this._validExpOperators]) {
      if (expOperator != null && !expOperatorsById.has(expOperator.id)) {
        expOperatorsById.set(expOperator.id, expOperator);
      }
    }
    return Array.from(expOperatorsById.values());
  }
  get roots() {
    return this._rootGroupNode.childrenToTreeData(this._selectedExpOperator, this._isSelectedExpOperatorValid);
  }
  get selectedValue() {
    if (this._selectedExpOperator != null) {
      const path = [this._selectedExpOperator];
      if (this._selectedExpOperator.groupID != null) {
        let groupNode = this.getGroupNode(this._selectedExpOperator.groupID);
        while (groupNode !== this._rootGroupNode) {
          path.unshift(groupNode.expOperatorGroup);
          groupNode = groupNode.parentGroupNodeOrThrow;
        }
      }
      return path;
    }
  }
  findOrCreateGroupNode(operatorGroupID) {
    if (operatorGroupID != null) {
      let groupNode = this.findGroupNode(operatorGroupID);
      if (groupNode == null) {
        const expOperatorGroup = this._expressionDomainModel.getExpOperatorGroup(operatorGroupID);
        groupNode = new OperatorTree.GroupNode(expOperatorGroup);
        this._groupNodesByOperatorGroupID.set(operatorGroupID, groupNode);
      }
      return groupNode;
    }
    return this._rootGroupNode;
  }
  findGroupNode(operatorGroupID) {
    return this._groupNodesByOperatorGroupID.get(operatorGroupID);
  }
  getGroupNode(operatorGroupID) {
    const groupNode = this.findGroupNode(operatorGroupID);
    Assert.assert(groupNode != null, `${operatorGroupID} absent from [${Array.from(this._groupNodesByOperatorGroupID.keys()).join(", ")}]`);
    return groupNode;
  }
  get expressionDomainModel() {
    return this._expressionDomainModel;
  }
}
(function (_OperatorTree2) {
  const AB_GEE_GROUP_ITEM = "ab-gee-operator-group-item";
  const AB_GEE_OPERATOR_ITEM = "ab-gee-operator-item";
  let Path;
  (function (_Path) {
    function isNotUnset(path) {
      if (path.length === 1 && path[0] == null) {
        return false;
      }
      Assert.assert(path.length > 0 && path.every(element => element != null));
      return true;
    }
    _Path.isNotUnset = isNotUnset;
    function getExpOperator(path) {
      Assert.assert(path.length > 0);
      const element = path[path.length - 1];
      Assert.assert(isValidatedExpOperator(element));
      return element;
    }
    _Path.getExpOperator = getExpOperator;
    function isValidatedExpOperator(element) {
      return "sourceOperandName" in element;
    }
    _Path.isValidatedExpOperator = isValidatedExpOperator;
    function unset(label) {
      return {
        value: AbEnriched.create(undefined, {
          className: AB_GEE_OPERATOR_ITEM,
          displayName: label,
          tooltip: label
        })
      };
    }
    _Path.unset = unset;
  })(Path || (Path = _OperatorTree2.Path || (_OperatorTree2.Path = {})));
  class GroupNode {
    constructor(expOperatorGroup) {
      _defineProperty(this, "expOperatorGroup", void 0);
      _defineProperty(this, "parentGroupNode", void 0);
      _defineProperty(this, "_childGroupNodes", void 0);
      _defineProperty(this, "_childExpOperators", void 0);
      this.expOperatorGroup = expOperatorGroup ?? {};
      this.parentGroupNode = undefined;
      this._childGroupNodes = [];
      this._childExpOperators = [];
    }
    get parentGroupNodeOrThrow() {
      Assert.assert(this.parentGroupNode != null);
      return this.parentGroupNode;
    }
    addChildGroupNode(groupNode) {
      Assert.assert(groupNode.parentGroupNode == null);
      groupNode.parentGroupNode = this;
      this._childGroupNodes.push(groupNode);
    }
    addChildExpOperator(expOperator) {
      this._childExpOperators.push(expOperator);
    }
    toTreeData(selectedExpOperator, isSelectedExpOperatorValid) {
      const children = this.childrenToTreeData(selectedExpOperator, isSelectedExpOperatorValid);
      const baseEnrichment = {
        className: AB_GEE_GROUP_ITEM,
        displayOnly: true
      };
      if ("label" in this.expOperatorGroup) {
        const labeledEnrichment = {
          ...baseEnrichment,
          displayName: this.expOperatorGroup.label,
          defaultExpanded: this.expOperatorGroup.defaultExpanded
        };
        return [{
          value: AbEnriched.create(this.expOperatorGroup, labeledEnrichment),
          children: children
        }];
      } else {
        return [{
          value: AB_ITEM_SEPARATOR
        }, ...children, {
          value: AB_ITEM_SEPARATOR
        }];
      }
    }
    childrenToTreeData(selectedExpOperator, isSelectedExpOperatorValid) {
      return [...this.childGroupNodesToTreeData(selectedExpOperator, isSelectedExpOperatorValid), ...this.childExpOperatorsToTreeData(selectedExpOperator, isSelectedExpOperatorValid)];
    }
    childGroupNodesToTreeData(selectedExpOperator, isSelectedExpOperatorValid) {
      return this._childGroupNodes.sort((a, b) => Expression.Domain.OperatorGroup.compare(a.expOperatorGroup, b.expOperatorGroup)).flatMap(childGroupNode => childGroupNode.toTreeData(selectedExpOperator, isSelectedExpOperatorValid));
    }
    childExpOperatorsToTreeData(selectedExpOperator, isSelectedExpOperatorValid) {
      return this._childExpOperators.filter(expOperator => {
        return expOperator.id === selectedExpOperator?.id ? isSelectedExpOperatorValid : expOperator.deprecated !== true;
      }).sort(Expression.Domain.Operator.compare).map(expOperator => ({
        value: AbEnriched.create(expOperator, {
          className: AB_GEE_OPERATOR_ITEM,
          displayName: Expression.Domain.Operator.getDisplayNameForDropdownItem(expOperator),
          tooltip: expOperator.description
        })
      }));
    }
  }
  _OperatorTree2.GroupNode = GroupNode;
})(OperatorTree || (OperatorTree = {}));
//# sourceMappingURL=OperatorTree.js.map
